// MAKECHANGE Framework + Leadership Assessment content
// Source: Dr. Sully's MAKECHANGE Leadership Assessment PDF

export const MAKECHANGE_PILLARS = [
  { letter: "M", key: "momentum", name: "Momentum", desc: "Disrupt stagnation. Create forward motion." },
  { letter: "A", key: "authority", name: "Authority", desc: "Lead with biblical truth and data-backed decisions." },
  { letter: "K", key: "kingdom", name: "Kingdom-Focus", desc: "Eternal truths. Ethical integrity." },
  { letter: "E", key: "equity", name: "Equity", desc: "Fair, accessible systems for all." },
  { letter: "C", key: "clarity", name: "Clarity", desc: "Cut through noise. Focus on what matters." },
  { letter: "H", key: "healing", name: "Healing", desc: "Trauma-informed, resilient leadership." },
  { letter: "A", key: "action", name: "Action", desc: "Scalable strategies tied to revenue." },
  { letter: "N", key: "navigation", name: "Navigation", desc: "Guide through transitions and AI adoption." },
  { letter: "G", key: "growth", name: "Growth", desc: "Cultivate the next generation." },
  { letter: "E", key: "empowerment", name: "Empowerment", desc: "Build capacity, not dependency." },
] as const;

export type PillarKey = (typeof MAKECHANGE_PILLARS)[number]["key"];

export const ASSESSMENT_QUESTIONS: {
  key: PillarKey;
  letter: string;
  pillar: string;
  prompt: string;
  ai: string;
}[] = [
  {
    key: "momentum",
    letter: "M",
    pillar: "Momentum",
    prompt:
      "I consistently take initiative to break through stagnation in my leadership, ministry, or business — even when conditions are not ideal. I create forward motion rather than waiting for permission.",
    ai: "Leaders with high momentum scores leverage automation and AI workflows to eliminate bottlenecks and maintain speed.",
  },
  {
    key: "authority",
    letter: "A",
    pillar: "Authority",
    prompt:
      "I lead with both spiritual conviction and professional competence. My decisions are grounded in biblical truth and supported by evidence, data, and strategic thinking.",
    ai: "Authoritative leaders use AI-driven insights to back up their decisions with data — combining discernment with analytics.",
  },
  {
    key: "kingdom",
    letter: "K",
    pillar: "Kingdom-Focus",
    prompt:
      "My strategies, goals, and innovations — including how I use technology — are aligned with Kingdom principles, eternal truths, and ethical integrity. Purpose comes before profit.",
    ai: "Kingdom-focused leaders ensure AI and technology serve the mission — deploying tools with ethical guardrails and stewardship.",
  },
  {
    key: "equity",
    letter: "E",
    pillar: "Equity",
    prompt:
      "I actively create environments where everyone has the opportunity to succeed. I build fair, accessible systems and address inequities in my organization, church, or community.",
    ai: "AI can identify systemic biases in data and processes — helping leaders build more equitable organizations at scale.",
  },
  {
    key: "clarity",
    letter: "C",
    pillar: "Clarity",
    prompt:
      "I cut through noise and confusion to focus on what truly matters. When something doesn't make sense, I make change — quickly and decisively. My communication is clear and direct.",
    ai: "AI-powered analytics filter distractions and surface the insights that matter — turning data overload into decision clarity.",
  },
  {
    key: "healing",
    letter: "H",
    pillar: "Healing",
    prompt:
      "I have done (or am actively doing) the inner work to heal from past trauma, disappointment, and burnout. I lead from a place of resilience, not unresolved pain.",
    ai: "AI automates administrative burdens that drain leaders — creating space for rest, reflection, and sustainable recovery.",
  },
  {
    key: "action",
    letter: "A",
    pillar: "Action",
    prompt:
      "I consistently translate vision into execution. My strategies produce measurable results — I don't just plan, I build. Every initiative is tied to growth, revenue, or tangible impact.",
    ai: "AI agents handle repetitive execution — content scheduling, lead nurturing, data entry — so you focus on high-impact moves.",
  },
  {
    key: "navigation",
    letter: "N",
    pillar: "Navigation",
    prompt:
      "I can guide myself and others through complex transitions — organizational change, technological shifts, and strategic pivots. I am not intimidated by the AI revolution; I am preparing for it.",
    ai: "AI-powered navigation means understanding and deploying AI tools strategically — from fundamentals to full agent deployment.",
  },
  {
    key: "growth",
    letter: "G",
    pillar: "Growth",
    prompt:
      "I am intentionally investing in the development of others — students, mentees, team members, or the next generation of leaders. I cultivate growth in those around me, not just in myself.",
    ai: "AI-powered learning paths and personalized development tools accelerate growth — teaching the next generation to lead with tech fluency.",
  },
  {
    key: "empowerment",
    letter: "E",
    pillar: "Empowerment",
    prompt:
      "I build capacity in others, not dependency on me. I equip people with the tools, frameworks, and confidence to lead independently — and I celebrate when they outgrow my coaching.",
    ai: "Empowered leaders give others AI tools and systems they own — no gatekeeping, no dependency, full capacity to scale their mission.",
  },
];

export const SCORE_TIERS = [
  {
    min: 80,
    max: 100,
    level: "Kingdom Catalyst",
    summary:
      "You are operating at a high level across most areas. You are ready to lead others, consult, and scale.",
    next: "Mentor others, integrate AI into your systems, and multiply your impact. Consider becoming a certified coach or consultant.",
  },
  {
    min: 60,
    max: 79,
    level: "Rising Leader",
    summary:
      "You have strong foundations but gaps that are costing you momentum, revenue, or influence. You are close to a breakthrough.",
    next: "Targeted coaching to close the gaps and build AI-powered systems for the areas where you scored lowest.",
  },
  {
    min: 40,
    max: 59,
    level: "Foundation Builder",
    summary:
      "You have the heart and the calling, but your systems, strategies, and self-care need serious work. You are building — and that is honorable.",
    next: "Invest in coaching, healing work, and foundational AI literacy to accelerate your growth.",
  },
  {
    min: 0,
    max: 39,
    level: "Starting Point",
    summary:
      "You are at the beginning of a transformation. That takes courage. Every great leader started somewhere.",
    next: "Book a discovery call immediately. You need a guide, a plan, and a community. We are here for that.",
  },
] as const;

export const tierFor = (score: number) =>
  SCORE_TIERS.find((t) => score >= t.min && score <= t.max) ?? SCORE_TIERS[3];
